import * as React from 'react';

import { now, toCalendarDateTime } from '@internationalized/date';
import { cn } from '@rematter/ui-web';
import { format } from 'date-fns';
import { useAtomValue } from 'jotai';
import { LucidePaperclip } from 'lucide-react';

import { $sentAttachments } from '../../_state/chatConversation.state';
import type {
  ChatMessagePart,
  ChatMessageRecord,
} from '../../_types/messageParts';
import type { SentAttachment } from '../../types';

import { PartRenderer } from './PartRenderer';

import { getLocale } from '~/shared/utils/locale';

interface MessageItemProps {
  message: ChatMessageRecord;
  showTimestamp?: boolean;
  setRef?: (messageId: string, element: HTMLDivElement | null) => void;
}

const intlFormatter = new Intl.DateTimeFormat(getLocale(), {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

type UserFilePartsProps = {
  parts: ChatMessagePart[];
  sentAttachments?: SentAttachment[];
};

//
// ⚛️ ⚛️ ⚛️ USER FILE PARTS ⚛️ ⚛️ ⚛️
//
const UserFileParts = ({ parts, sentAttachments }: UserFilePartsProps) => {
  const items: { key: string; filename: string; url?: string }[] = [];

  for (const part of parts) {
    if (part.data.type === 'File') {
      items.push({
        key: part.id,
        filename: part.data.filename,
        url: part.data.url || undefined,
      });
    }
  }

  if (items.length === 0 && sentAttachments) {
    for (let i = 0; i < sentAttachments.length; i++) {
      items.push({
        key: `sent-${i}`,
        filename: sentAttachments[i].fileName,
      });
    }
  }

  if (items.length === 0) return null;

  return (
    <div className="@UserFileParts flex flex-wrap justify-end gap-1.5">
      {items.map((item) => {
        const chip = (
          <div className="flex items-center gap-1.5 rounded-md border border-primary/20 bg-primary/5 px-2 py-1 text-xs text-primary">
            <LucidePaperclip size={12} />
            <span className="max-w-[200px] truncate">{item.filename}</span>
          </div>
        );

        if (item.url) {
          return (
            <a
              key={item.key}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80"
            >
              {chip}
            </a>
          );
        }

        return <div key={item.key}>{chip}</div>;
      })}
    </div>
  );
};

type TextPartsProps = {
  parts: ChatMessagePart[];
  isUser: boolean;
};

//
// ⚛️ ⚛️ ⚛️ TEXT PARTS ⚛️ ⚛️ ⚛️
//
const TextParts = ({ parts, isUser }: TextPartsProps) => {
  if (parts.length === 0) return null;

  if (isUser) {
    return (
      <div className="rounded-lg bg-primary p-3 text-white">
        {parts.map((part) => (
          <div key={part.id}>
            <PartRenderer part={part} isUser />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="@TextParts flex flex-col gap-2">
      {parts.map((part) => (
        <div key={part.id}>
          <PartRenderer part={part} isUser={false} />
        </div>
      ))}
    </div>
  );
};

type FilePartsProps = {
  parts: ChatMessagePart[];
  isUser: boolean;
  sentAttachments?: SentAttachment[];
};

//
// ⚛️ ⚛️ ⚛️ FILE PARTS ⚛️ ⚛️ ⚛️
//
const FileParts = ({ parts, isUser, sentAttachments }: FilePartsProps) => {
  if (isUser) {
    return <UserFileParts parts={parts} sentAttachments={sentAttachments} />;
  }

  return (
    <>
      {parts.map((part) => (
        <PartRenderer key={part.id} part={part} isUser={false} />
      ))}
    </>
  );
};

//
// ⚛️ ⚛️ ⚛️ MESSAGE ITEM ⚛️ ⚛️ ⚛️
//
export const MessageItem = ({
  message,
  showTimestamp = true,
  setRef,
}: MessageItemProps) => {
  const isUser = message.role === 'User';

  // Atoms
  const sentAttachmentsMap = useAtomValue($sentAttachments);

  // Memoized values
  const messageText = React.useMemo(
    () =>
      message.parts
        .filter(
          (
            p,
          ): p is ChatMessagePart & { data: { type: 'Text'; text: string } } =>
            p.data.type === 'Text',
        )
        .map((p) => p.data.text)
        .join('')
        .trim(),
    [message.parts],
  );

  const lookupAttachments = React.useMemo(() => {
    if (!isUser) return undefined;
    const key = `${message.chatId}:${messageText}`;
    return sentAttachmentsMap.get(key);
  }, [isUser, message.chatId, messageText, sentAttachmentsMap]);

  const formatedTime = React.useMemo(() => {
    const isSameDay =
      toCalendarDateTime(message.createdAt).compare(
        toCalendarDateTime(now(message.createdAt.timeZone)),
      ) === 0;

    if (isSameDay) {
      return format(message.createdAt.toDate(), 'hh:mm:ss');
    } else {
      return intlFormatter.format(message.createdAt.toDate());
    }
  }, [message]);

  const { textParts, fileParts, toolCallParts, toolResultParts, otherParts } =
    React.useMemo(() => {
      const text: ChatMessagePart[] = [];
      const file: ChatMessagePart[] = [];
      const toolCall: ChatMessagePart[] = [];
      const toolResult: ChatMessagePart[] = [];
      const other: ChatMessagePart[] = [];

      // NOTE: the commented out parts are just hiding from the user. Kept the code as reference
      // in case we want to bring them back.
      for (const p of message.parts) {
        switch (p.data.type) {
          case 'Text':
            text.push(p);
            break;
          case 'File':
            file.push(p);
            break;
          case 'ToolCall':
            // toolCall.push(p);
            break;
          case 'DynamicTool':
            // toolResult.push(p);
            break;
          case 'Reasoning':
            // reasoning.push(p);
            break;
          default:
            other.push(p);
            break;
        }
      }

      return {
        textParts: text,
        fileParts: file,
        toolCallParts: toolCall,
        toolResultParts: toolResult,
        otherParts: other,
      };
    }, [message.parts]);

  const hasVisibleContent = React.useMemo(() => {
    if (textParts.length > 0 || fileParts.length > 0 || otherParts.length > 0) {
      return true;
    }
    const hasActionButtons = toolCallParts.some(
      (p) =>
        p.data.type === 'ToolCall' &&
        p.data.toolName === 'general-userInput' &&
        Array.isArray(p.data.input?.options) &&
        p.data.input.options.length > 0,
    );
    if (hasActionButtons) return true;
    const hasToolErrors = toolResultParts.some(
      (p) =>
        p.data.type === 'DynamicTool' && p.data.toolCallState === 'OutputError',
    );
    return hasToolErrors;
  }, [textParts, fileParts, otherParts, toolCallParts, toolResultParts]);

  // Callbacks
  const refCallback = React.useCallback(
    (element: HTMLDivElement | null) => {
      if (setRef) {
        setRef(message.id, element);
      }
    },
    [message.id, setRef],
  );

  if (!hasVisibleContent) {
    return null;
  }

  return (
    <div
      ref={setRef ? refCallback : undefined}
      className={cn(
        '@MessageItem flex',
        isUser && 'justify-end',
        !isUser && 'justify-start',
      )}
    >
      <div
        className={cn(
          'flex flex-col gap-2',
          isUser && 'max-w-[80%]',
          !isUser && 'w-full',
        )}
      >
        <TextParts parts={textParts} isUser={isUser} />

        <FileParts
          parts={fileParts}
          isUser={isUser}
          sentAttachments={lookupAttachments}
        />

        {toolCallParts.map((part) => (
          <PartRenderer key={part.id} part={part} isUser={isUser} />
        ))}

        {toolResultParts.map((part) => (
          <PartRenderer key={part.id} part={part} isUser={isUser} />
        ))}

        {otherParts.map((part) => (
          <PartRenderer key={part.id} part={part} isUser={isUser} />
        ))}

        {showTimestamp && (
          <div
            className={cn(
              'text-xs text-neutral/60',
              isUser && 'text-right',
              !isUser && 'text-left',
            )}
          >
            {formatedTime}
          </div>
        )}
      </div>
    </div>
  );
};
